import { Button, CircularProgress, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import CallIcon from '@mui/icons-material/Call';
import { Alert } from '../../../../shared/lib/common/Alert';
import { withTrademark, withTrademarkTM } from '../../../../shared/lib/common/Tokenizer';
import {
  NotificationType,
  Plan,
  RingMyPhoneLogs,
  useGetSubscriptionDetailLazyQuery,
  useRingMyPhoneLazyQuery,
} from '../../generated';
import {
  BUTTON_TEXT_CLOSE,
} from '../../../../shared/lib/constants';
import {
  BUTTON_TEXT_CONFIRM,
  LABEL_RING_MY_PHONE_TM,
  MSG_CONFIRM_RING_PHONE,
  MSG_RMP_INITIATED,
  MSG_RMP_INITIATION_FAILED,
  TOOLTIP_ENABLE_PHONE_RINGER_FOR_LOCATION,
  TOOLTIP_FREE_PLAN_FEATURE_UNAVAILABLE,
  TOOLTIP_NO_VIP_CONTACT,
} from '../../app/constants';
import { useAppContext } from '../../app/contexts/AppContext';
import { ActionType } from '../../app/reducer/AppContextReducer';
import ConfirmationDialog from '../common/CustomConfirmationDialog';
import RingMyPhoneLockModal from '../dashboard/RingMyPhoneLockModal';
import AppNotificationModal from '../dashboard/AppNotificationModal';
import CallModal from '../dashboard/CallModal';

interface Props {
  loading?: boolean;
}

export const RingMyPhoneButton = ({ loading: externalLoading = false }: Props) => {
  const { currentUser, cardsData, dispatch } = useAppContext();
  const { isSubscribed, enablePhoneRinger } = currentUser ?? {};
  const { customerStripeId, subscriptionCancelled } = isSubscribed ?? {};

  const [subscriptionPlan, setSubscriptionPlan] = useState<Plan | null>(null);
  const [openNotification, setOpenNotification] = useState(false);
  const [ringLog, setRingLog] = useState<RingMyPhoneLogs | null>(null);
  const [openCall, setOpenCall] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [cooldownActive, setCooldownActive] = useState(false);
  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const [getSubscriptionDetail] = useGetSubscriptionDetailLazyQuery({
    onError({ message }) {
      Alert.error(message);
    },
    onCompleted(data) {
      const { getSubscriptionDetail } = data;
      if (getSubscriptionDetail) {
        const { data: detailData } = getSubscriptionDetail;
        const { items } = detailData[0] ?? {};
        const { data: itemsData } = items ?? {};
        const { plan } = itemsData.slice(-1)[0] ?? {};
        setSubscriptionPlan(plan);
        const { metadata } = plan;
        const { contacts } = metadata;
        dispatch({ type: ActionType.SET_CONTACT_COUNT, contactCount: parseInt(contacts) });
      }
    }
  });

  const [ringMyPhone, { loading: ringLoading }] = useRingMyPhoneLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: async (data) => {
      const { ringMyPhone } = data;
      if (ringMyPhone) {
        localStorage.setItem('ringCooldownStart', Date.now().toString());
        setCooldownActive(true);
        setRingLog(ringMyPhone);
        Alert.success(MSG_RMP_INITIATED);
        if (ringMyPhone.notificationType === NotificationType.AppNotification) {
          setOpenNotification(true);
        } else if (ringMyPhone.notificationType === NotificationType.PhoneCall) {
          setOpenCall(true);
        }
      } else {
        Alert.error(MSG_RMP_INITIATION_FAILED);
      }
    },
    onError: ({ message }) => {
      Alert.error(message);
    }
  });

  const handleRingMyPhone = async () => {
    await ringMyPhone();
    setOpen(false);
  };

  useEffect(() => {
    if (customerStripeId) {
      getSubscriptionDetail({ variables: { customerStripeId } });
    }
  }, [customerStripeId]);

  useEffect(() => {
    const startTime = localStorage.getItem('ringCooldownStart');
    if (!startTime) return;

    const cooldownPeriod = Number(import.meta.env.VITE_COOL_DOWN_PERIOD) * 1000;
    const elapsed = Date.now() - parseInt(startTime);

    if (elapsed >= cooldownPeriod) {
      localStorage.removeItem('ringCooldownStart');
      setCooldownActive(false);
      setCooldown(0);
      return;
    }

    setCooldownActive(true);
    setCooldown(Math.ceil((cooldownPeriod - elapsed) / 1000));

    const interval = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCooldownActive(false);
          localStorage.removeItem('ringCooldownStart');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldownActive]);

  const isFreePlan = subscriptionPlan?.nickname === 'Free plan';

  return (
    <>
      <Tooltip
        title={
          isFreePlan
            ? TOOLTIP_FREE_PLAN_FEATURE_UNAVAILABLE
            : !enablePhoneRinger
              ? withTrademarkTM(TOOLTIP_ENABLE_PHONE_RINGER_FOR_LOCATION)
              : cardsData?.length === 0
                ? TOOLTIP_NO_VIP_CONTACT
                : ''
        }
      >
        <span>
          <Button
            variant="outlined"
            startIcon={<CallIcon />}
            endIcon={(externalLoading || ringLoading) && <CircularProgress size={20} />}
            onClick={() => {
              if (isFreePlan) {
                setOpenModal(true);
              } else {
                setOpen(true);
              }
            }}
            disabled={
              !isFreePlan &&
              (externalLoading || cooldownActive || ringLoading || cardsData?.length === 0 || !enablePhoneRinger || (subscriptionCancelled ?? false))
            }
          >
            {cooldownActive ? `Try again in ${cooldown}s` : withTrademarkTM(LABEL_RING_MY_PHONE_TM)}
          </Button>
        </span>
      </Tooltip>

      <ConfirmationDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={handleRingMyPhone}
        title={withTrademark(LABEL_RING_MY_PHONE_TM)}
        confirmText={BUTTON_TEXT_CONFIRM}
        cancelText={BUTTON_TEXT_CLOSE}
        message={MSG_CONFIRM_RING_PHONE}
        loading={externalLoading || ringLoading}
      />

      {openModal && <RingMyPhoneLockModal open={openModal} onClose={() => setOpenModal(false)} />}
      {openNotification && ringLog && <AppNotificationModal onClose={() => setOpenNotification(false)} ringMyPhoneLog={ringLog} />}
      {openCall && ringLog && <CallModal open={openCall} onClose={() => setOpenCall(false)} ringMyPhoneLog={ringLog} />}
    </>
  );
};
